import { Component, input, output } from '@angular/core';
import { InAppBrowser, ToolBarType } from '@capgo/capacitor-inappbrowser';
import { CsatResponse } from 'src/app/models/csat.model';
import { CoreService } from 'src/app/services/core/core.service';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { LoaderService } from 'src/app/services/loader/loader.service';

@Component({
  selector: 'app-csat-details-card',
  templateUrl: './csat-details-card.component.html',
  styleUrls: ['./csat-details-card.component.scss'],
  standalone: false,
})
export class CsatDetailsCardComponent {
  /* CSAT survey details used to render card content and redirection data */
  csatDetails = input<CsatResponse | null>(null);

  /* Emits event when survey is successfully marked as visited */
  markSurveyVisited = output<void>();

  /* Guards repeated tap/click while redirect is being initialized */
  isRedirectInProgress = false;

  constructor(
    private readonly coreService: CoreService,
    private readonly dashboardService: DashboardService,
    private readonly loaderService: LoaderService
  ) { }

  /* Redirects user to CSAT survey based on platform */
  async redirectToSurvey(): Promise<void> {
    if (this.isRedirectInProgress) {
      return;
    }

    this.isRedirectInProgress = true;
    this.loaderService.showLoader();

    if (this.coreService.isMobileApp) {
      await this.redirectToSurveyMobile();
    } else {
      this.redirectToSurveyWeb();
    }
  }

  /* Handles CSAT redirection flow for web browsers */
  private redirectToSurveyWeb(): void {
    const csat = this.csatDetails();

    if (!csat?.surveY_REQUEST_URL || !csat?.surveY_FORM_POST_DATA) {
      this.isRedirectInProgress = false;
      this.loaderService.hideLoader();
      return;
    }

    let parsedFormData: Record<string, string>;
    try {
      parsedFormData = JSON.parse(csat.surveY_FORM_POST_DATA);
    } catch {
      this.isRedirectInProgress = false;
      this.loaderService.hideLoader();
      return;
    }

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = csat.surveY_REQUEST_URL;
    form.target = '_blank';

    Object.entries(parsedFormData).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = String(value);
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    form.remove();

    this.isRedirectInProgress = false;
    this.loaderService.hideLoader();

    this.setSurveyVisited(csat?.surveY_REQUEST_ID ?? '');
  }

  /* Persists survey visited state on backend */
  private setSurveyVisited(id: string): void {
    this.dashboardService.setSurveyVisited(id).subscribe(() => {
      this.markSurveyVisited.emit();
    });
  }

  /* Handles CSAT redirection flow for mobile (Capacitor).
     Builds an auto-submitting HTML form and loads it in the InAppBrowser so
     that all POST fields (po number, etc.) are sent exactly like the web path. */
  private async redirectToSurveyMobile(): Promise<void> {
    try {
      const csat = this.csatDetails();

      if (!csat?.surveY_REQUEST_URL || !csat?.surveY_FORM_POST_DATA) {
        this.isRedirectInProgress = false;
        this.loaderService.hideLoader();
        return;
      }

      const url = csat.surveY_REQUEST_URL;
      const formData: Record<string, string> = JSON.parse(csat.surveY_FORM_POST_DATA);

      const hiddenInputs = Object.entries(formData)
        .map(([key, value]) =>
          `<input type="hidden" name="${this.escapeHtml(key)}" value="${this.escapeHtml(String(value))}">`
        )
        .join('');

      const html =
        `<html><body>` +
        `<form id="csatForm" method="POST" action="${this.escapeHtml(url)}">${hiddenInputs}</form>` +
        `<script>document.getElementById('csatForm').submit();</script>` +
        `</body></html>`;

      const dataUrl = `data:text/html;charset=utf-8,${encodeURIComponent(html)}`;

      await InAppBrowser.openWebView({
        url: dataUrl,
        toolbarType: ToolBarType.NAVIGATION,
        title: '',
      });

      this.isRedirectInProgress = false;
      this.loaderService.hideLoader();

      this.setSurveyVisited(csat?.surveY_REQUEST_ID ?? '');
    } catch (error) {
      console.error('Survey redirect failed', error);
      this.isRedirectInProgress = false;
      this.loaderService.hideLoader();
      throw error;
    }
  }

  /* Escapes characters that are special inside HTML attribute values */
  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

}